'use client';

import { LoadingDark } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { useCallback, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useVisitorCurationsInfiniteQuery } from '../../common/hooks/useVisitorCurationsInfiniteQuery';
import { CurationItem } from './CurationItem';
import { EmptyShopCurationList } from './EmptyShopCurationList';

interface Props {
  snsTitle: string;
  curationId?: string;
  type?: 'GOODS' | 'SNAP2';
}
export const CurationList = ({ snsTitle, curationId, type }: Props) => {
  const { products, hasNextPage, isFetchingNextPage, fetchNextPage, isPending, isError } =
    useVisitorCurationsInfiniteQuery({ snsTitle, curationId, size: 5, type });
  const { isWebview } = useDeviceInfo();

  const [pageCache, setPageCache] = useState<Map<string, number>>(new Map());

  const onEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderItem = useCallback(
    (index: number) => {
      const curation = products[index];
      if (!curation) return null;

      return (
        <CurationItem
          key={curation.curationId}
          snsTitle={snsTitle}
          item={curation}
          pageCache={pageCache}
          setPageCache={setPageCache}
          curationIndex={index}
        />
      );
    },
    [products, pageCache, setPageCache],
  );

  if (isPending) {
    return isWebview ? null : (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  if (isError || products.length === 0) {
    return <EmptyShopCurationList />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      <Virtuoso
        style={{ height: '100%' }}
        useWindowScroll
        totalCount={products.length}
        itemContent={renderItem}
        endReached={onEndReached}
        overscan={2}
        increaseViewportBy={{ top: 500, bottom: 500 }}
      />
    </div>
  );
};
